import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import CheckIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import {
  getMessageClassification,
  confirmCustomerField,
  correctCustomerField,
  revealCustomerField,
  getMailOrbitErrorKey
} from 'src/utils/MailOrbitService';
import useStyles from './useStyles';

// [classification anahtarı, API alan adı, i18n anahtarı]
const FIELDS = [
  ['firstName', 'FirstName', 'field_first_name'],
  ['lastName', 'LastName', 'field_last_name'],
  ['address', 'Address', 'field_address'],
  ['contactPhone', 'ContactPhone', 'field_contact_phone']
];

// Gösterilen değer bu süre sonra otomatik yeniden maskelenir.
const REVEAL_TIMEOUT_MS = 60000;

/**
 * Seçili mesajın maskeli müşteri alanları; göster (reveal) / onayla / düzelt (reject kapsam dışı).
 * Revealed değer yalnızca bu bileşenin state'inde tutulur; Redux/localStorage/log'a yazılmaz.
 */
const CustomerFields = ({ emailMessageId, canWork }) => {
  const classes = useStyles();
  const { t } = useTranslation(['mailorbit_workorder', 'mailorbit']);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [editing, setEditing] = useState(null); // API alan adı
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState({}); // API alan adı -> açık değer (yalnızca bellek)
  const [revealing, setRevealing] = useState({}); // API alan adı -> true (istek uçuşta)
  const [fieldErrors, setFieldErrors] = useState({}); // API alan adı -> hata anahtarı
  const [expiries, setExpiries] = useState({}); // API alan adı -> yeniden maskelenme zamanı (ms)
  const [now, setNow] = useState(Date.now());
  const timers = useRef({});
  const requestSeq = useRef(0);

  const clearRevealed = useCallback(() => {
    requestSeq.current += 1; // uçuştaki reveal yanıtlarını geçersiz kıl
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
    setRevealed({});
    setExpiries({});
    setRevealing({});
    setFieldErrors({});
  }, []);

  const hideField = useCallback(apiField => {
    clearTimeout(timers.current[apiField]);
    delete timers.current[apiField];
    setRevealed(prev => {
      const next = { ...prev };
      delete next[apiField];
      return next;
    });
    setExpiries(prev => {
      const next = { ...prev };
      delete next[apiField];
      return next;
    });
  }, []);

  const setFieldError = (apiField, key) =>
    setFieldErrors(prev => {
      const next = { ...prev };
      if (key) next[apiField] = key;
      else delete next[apiField];
      return next;
    });

  const reveal = async (id, apiField) => {
    const seq = requestSeq.current;
    setRevealing(prev => ({ ...prev, [apiField]: true }));
    setFieldError(apiField, null);
    try {
      const res = await revealCustomerField(id, apiField);
      if (seq !== requestSeq.current) return; // mesaj/konuşma değişti
      setRevealed(prev => ({ ...prev, [apiField]: res && res.value }));
      setExpiries(prev => ({ ...prev, [apiField]: Date.now() + REVEAL_TIMEOUT_MS }));
      setNow(Date.now());
      clearTimeout(timers.current[apiField]);
      timers.current[apiField] = setTimeout(() => hideField(apiField), REVEAL_TIMEOUT_MS);
    } catch (e) {
      if (seq === requestSeq.current) setFieldError(apiField, getMailOrbitErrorKey(e));
    } finally {
      if (seq === requestSeq.current) {
        setRevealing(prev => {
          const next = { ...prev };
          delete next[apiField];
          return next;
        });
      }
    }
  };

  // Geri sayım için saniyelik tik; yalnızca açık değer varken çalışır.
  const hasRevealed = Object.keys(expiries).length > 0;
  useEffect(() => {
    if (!hasRevealed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasRevealed]);

  // Mesaj/konuşma değişince ya da panel kapanınca açık değerleri sil.
  useEffect(() => {
    clearRevealed();
    return clearRevealed;
  }, [emailMessageId, clearRevealed]);

  const load = useCallback(async () => {
    if (!emailMessageId) return;
    setLoading(true);
    setErrorKey(null);
    try {
      setSummary(await getMessageClassification(emailMessageId));
    } catch (e) {
      setSummary(null);
      setErrorKey(getMailOrbitErrorKey(e));
    } finally {
      setLoading(false);
    }
  }, [emailMessageId]);

  useEffect(() => {
    setEditing(null);
    load();
  }, [load]);

  const run = async action => {
    setBusy(true);
    setErrorKey(null);
    try {
      await action();
      clearRevealed(); // onay/düzeltme sonrası eski açık değer bayatlar
      setEditing(null);
      await load();
    } catch (e) {
      setErrorKey(getMailOrbitErrorKey(e));
    } finally {
      setBusy(false);
    }
  };

  const details = summary && summary.customerDetails;

  const fieldLabel = apiField => {
    const entry = FIELDS.find(f => f[1] === apiField);
    return entry ? t(`mailorbit_workorder:${entry[2]}`) : apiField;
  };

  // Değeri olan alanlar; "tümü" işlemleri bunlar üzerinde çalışır.
  const actionableFields = details
    ? FIELDS.filter(([key]) => details[key] && details[key].hasValue)
    : [];

  const showAll = () => {
    actionableFields
      .filter(([, apiField]) => revealed[apiField] === undefined && !revealing[apiField])
      .forEach(([, apiField]) => reveal(details.id, apiField));
  };

  const confirmAll = async () => {
    setBusy(true);
    setErrorKey(null);
    setFieldErrors({});
    const failures = {};
    await Promise.all(
      actionableFields
        .filter(([key]) => details[key].verificationState !== 'Confirmed')
        .map(([, apiField]) =>
          confirmCustomerField(details.id, apiField).catch(e => {
            failures[apiField] = getMailOrbitErrorKey(e);
          })
        )
    );
    clearRevealed(); // onay sonrası eski açık değer bayatlar
    setFieldErrors(failures);
    setEditing(null);
    await load();
    setBusy(false);
  };

  const anyRevealing = Object.keys(revealing).length > 0;
  const allConfirmed = actionableFields.every(
    ([key]) => details[key].verificationState === 'Confirmed'
  );

  return (
    <Box className={classes.sideSection}>
      {loading && <CircularProgress size={18} />}
      {errorKey && <Alert severity="error">{t(`mailorbit:${errorKey}`)}</Alert>}
      {Object.keys(fieldErrors).map(apiField => (
        <Alert
          key={apiField}
          severity="error"
          style={{ marginBottom: 4 }}
          onClose={() => setFieldError(apiField, null)}
        >
          {`${fieldLabel(apiField)}: ${t(`mailorbit:${fieldErrors[apiField]}`)}`}
        </Alert>
      ))}
      {!loading && !errorKey && !details && (
        <Typography variant="body2" color="textSecondary">
          {t('mailorbit_workorder:no_customer_fields')}
        </Typography>
      )}
      {details &&
        FIELDS.map(([key, apiField, labelKey]) => {
          const f = details[key];
          if (!f) return null;
          return (
            <Box key={key} className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>
                {t(`mailorbit_workorder:${labelKey}`)}
              </Typography>
              {editing === apiField ? (
                <Box className={classes.fieldEdit}>
                  <TextField
                    size="small"
                    variant="outlined"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                  />
                  <Button
                    size="small"
                    color="primary"
                    disabled={busy || !draft.trim()}
                    onClick={() =>
                      run(() => correctCustomerField(details.id, apiField, draft.trim()))
                    }
                  >
                    {t('mailorbit_workorder:save')}
                  </Button>
                  <Button size="small" disabled={busy} onClick={() => setEditing(null)}>
                    {t('mailorbit_workorder:cancel')}
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography className={classes.fieldValue}>
                    {f.hasValue ? (revealed[apiField] !== undefined ? revealed[apiField] : f.maskedValue) : '-'}{' '}
                    {f.verificationState && f.verificationState !== 'Unverified' && (
                      <Chip
                        size="small"
                        className={f.verificationState === 'Confirmed' ? classes.stateConfirmed : undefined}
                        label={t(
                          `mailorbit_workorder:state_${f.verificationState}`,
                          f.verificationState
                        )}
                      />
                    )}
                  </Typography>
                  {canWork && f.hasValue && (
                    <Box className={classes.fieldTools}>
                      <Tooltip
                        title={
                          revealed[apiField] !== undefined
                            ? t('mailorbit_workorder:hide')
                            : t('mailorbit_workorder:reveal')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            className={classes.fieldIconBtn}
                            aria-label={`${
                              revealed[apiField] !== undefined
                                ? t('mailorbit_workorder:hide')
                                : t('mailorbit_workorder:reveal')
                            }: ${t(`mailorbit_workorder:${labelKey}`)}`}
                            disabled={busy || !!revealing[apiField]}
                            onClick={() =>
                              revealed[apiField] !== undefined
                                ? hideField(apiField)
                                : reveal(details.id, apiField)
                            }
                          >
                            {revealing[apiField] ? (
                              <CircularProgress size={16} />
                            ) : revealed[apiField] !== undefined ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {revealed[apiField] !== undefined && expiries[apiField] && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          title={t('mailorbit_workorder:hides_in', {
                            count: Math.max(0, Math.ceil((expiries[apiField] - now) / 1000))
                          })}
                        >
                          {Math.max(0, Math.ceil((expiries[apiField] - now) / 1000))}
                          {t('mailorbit_workorder:seconds_short')}
                        </Typography>
                      )}
                      <Tooltip title={t('mailorbit_workorder:confirm')}>
                        <span>
                          <IconButton
                            size="small"
                            className={classes.fieldIconBtn}
                            aria-label={`${t('mailorbit_workorder:confirm')}: ${t(
                              `mailorbit_workorder:${labelKey}`
                            )}`}
                            disabled={busy || f.verificationState === 'Confirmed'}
                            onClick={() => run(() => confirmCustomerField(details.id, apiField))}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('mailorbit_workorder:correct')}>
                        <span>
                          <IconButton
                            size="small"
                            className={classes.fieldIconBtn}
                            aria-label={`${t('mailorbit_workorder:correct')}: ${t(
                              `mailorbit_workorder:${labelKey}`
                            )}`}
                            disabled={busy}
                            onClick={() => {
                              setDraft('');
                              setEditing(apiField);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </>
              )}
            </Box>
          );
        })}
      {canWork && actionableFields.length > 0 && (
        <Box className={classes.fieldBulk}>
          <Button size="small" variant="outlined" disabled={busy || anyRevealing} onClick={showAll}>
            {t('mailorbit_workorder:show_all')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={busy || allConfirmed}
            onClick={confirmAll}
          >
            {t('mailorbit_workorder:confirm_all')}
          </Button>
        </Box>
      )}
      {canWork && actionableFields.length > 0 && (
        <Typography variant="caption" color="textSecondary" component="div" style={{ color: '#5b6b75' }}>
          {t('mailorbit_workorder:reveal_hint')}
        </Typography>
      )}
    </Box>
  );
};

CustomerFields.propTypes = {
  emailMessageId: PropTypes.string,
  canWork: PropTypes.bool
};

export default CustomerFields;
